import React from 'react';
import { FieldError } from 'react-hook-form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface CustomInputProps {
  label?: string;
  error?: FieldError;
  type?: string;
  placeholder?: string;
  value?: string | null;
  name?: string;
  autoComplete?: string;
  id?: string;
  helperText?: string;
  containerClassName?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  maxLength?: number;
  fullWidth?: boolean;
  inputSize?: 'default' | 'xl';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  accept?: string;
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      label,
      error,
      value,
      onChange,
      onBlur,
      onKeyDown,
      name,
      type,
      autoComplete,
      className,
      helperText,
      placeholder,
      containerClassName,
      disabled,
      min,
      fullWidth,
      prefix,
      suffix,
      ...props
    },
    ref
  ) => {
    return (
      <div className={containerClassName || 'mb-4'}>
        {label && (
          <Label htmlFor={props.id || name} className='mb-2'>
            {label}
          </Label>
        )}
        <div className='relative w-full'>
          {prefix && (
            <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10'>
              {prefix}
            </div>
          )}
          <Input
            ref={ref}
            id={props.id || name}
            {...(type !== 'file' && value !== undefined
              ? { value: value ?? '' }
              : {})}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            name={name}
            className={`${prefix ? 'pl-10' : ''} ${suffix ? 'pr-10' : ''} ${className || ''}`}
            placeholder={placeholder}
            type={type || 'text'}
            autoComplete={autoComplete}
            disabled={disabled}
            min={min}
            fullWidth={fullWidth}
            {...props}
          />
          {suffix && (
            <div className='absolute right-1 top-1/2 -translate-y-1/2 z-10'>
              {suffix}
            </div>
          )}
        </div>
        {helperText && (
          <div className='text-gray-500 text-xs mt-1 ml-1 text-align-left'>
            {helperText}
          </div>
        )}
        {error && (
          <div className='text-red-500 text-xs mt-1 ml-1 text-align-left'>
            {typeof error === 'string' ? error : error.message}
          </div>
        )}
      </div>
    );
  }
);

CustomInput.displayName = 'CustomInput';

export default CustomInput;
